import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import {
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDiagnosticsService,
  RemoteDiagnosticsStore,
} from '@store';
import { DiagnosticsUrlTypeEnum } from '../../+installations/installation/installation-overlay.model';

import { RoutingService } from './routing.service';

@Injectable()
export class RemoteDiagnosticsGuardService {
  constructor(
    private installationDetailsStore: InstallationDetailsStore,
    private routingService: RoutingService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private remoteDiagnosticsService: RemoteDiagnosticsService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.remoteDiagnosticsService.getRemoteDiagnosticsAccessAndConnectionStatus().pipe(
      switchMap(({ hasAccessToDiagnostics, isDiagnosticsSessionRunning }) => {
        // User has no possibility to start a RD session and session was started by someone else
        if (!hasAccessToDiagnostics && isDiagnosticsSessionRunning) {
          return of(true);
        }
        if (!hasAccessToDiagnostics && !isDiagnosticsSessionRunning) {
          this.navigateToInstallationDashboard(state);
        }

        return this.handleRemoteDiagnosticsSpecificUrl(
          route.routeConfig.path,
          route.queryParams?.gatewaySerial
        ).pipe(
          tap((isAllowed) => {
            if (!isAllowed) {
              this.navigateToInstallationDashboard(state);
            }
          })
        );
      })
    );
  }

  private handleRemoteDiagnosticsSpecificUrl(
    path: string,
    gatewaySerial: string
  ): Observable<boolean> {
    return combineLatest([
      this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(gatewaySerial),
      // wait for active sessions to be read ( withouht this main LL from Topology will always be empty )
      this.remoteDiagnosticsStore.activeSessionStatusChecked$.pipe(filter((value) => value)),
      this.installationFeaturesStore.isFeaturesRequestFinished$.pipe(
        filter((isFinished) => isFinished)
      ),
    ]).pipe(
      take(1),
      switchMap(([mainLogicalLinkFromTopology]) => {
        switch (path) {
          case DiagnosticsUrlTypeEnum.Backups: {
            return this.installationDetailsStore.isBackupMenuItemVisible(gatewaySerial);
          }
          case DiagnosticsUrlTypeEnum.ExtendedParameter: {
            return this.installationDetailsStore.isExtendedParametersMenuItemVisible(gatewaySerial);
          }
          case DiagnosticsUrlTypeEnum.GuidedTroubleshooting: {
            return this.installationDetailsStore.isGtsMenuItemVisible(gatewaySerial);
          }
          case DiagnosticsUrlTypeEnum.HydraulicSchematic: {
            if (mainLogicalLinkFromTopology) {
              return this.installationDetailsStore.isHydraulicSchemeMenuItemVisible(gatewaySerial);
            } else {
              return of(false);
            }
          }
          case DiagnosticsUrlTypeEnum.Snapshots: {
            return this.installationDetailsStore.isSnapshotMenuItemVisible(gatewaySerial);
          }
        }
      })
    );
  }

  private navigateToInstallationDashboard(state: RouterStateSnapshot): void {
    this.routingService.dropChildPathFromUrl();
    this.routingService.navigateToInstallationDashboardFromGuard(state);
  }
}
